import React, { useCallback, useEffect, useState } from 'react';
import Toolbar from './Toolbar';
import Pagination from 'reusable/pagination';
import DriverTable from './DriverTable';
import axiosInstance from 'config/axiosInstance';
import { CsvCondition } from 'config/csv';
import { useTranslation } from 'react-i18next';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { pageToRecover } from 'reusable/pageRecovery';
import { PageSizeSelect, usePageSize } from 'reusable/PageSize';

const DriverManagement: React.FC = () => {
  const { t } = useTranslation('common');
  // EV-493。1ページの件数（20・50・100、初めは 50。localStorage に覚える）。以前は 10件で固定。
  const [rowsPerPage, setRowsPerPage] = usePageSize('driverList.pageSize');
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [driver, setDriver] = useState<any[]>([]);
  // EV-142。次の検索を待つ間も、前の行には前の取得条件を付ける。
  const [exportConditions, setExportConditions] = useState<CsvCondition[]>([['page', 1]]);
  const [loading, setLoading] = useState(false);
  // ADM-15。一覧の取得に失敗したことを、空の一覧と区別して出す。
  const [fetchError, setFetchError] = useState<unknown>(null);
  // ADM-15。最後に送った要求の応答だけを反映する（遅れて届いた古い検索結果で上書きしない）。
  const beginRequest = useLatestRequest();

  // 取り直し（ADM-14）では一覧を読み込み中表示に切り替えない。
  // 切り替えると表の中に出しているエラー表示が消えるため。
  const fetchDriverData = useCallback(async (showLoading: boolean = true) => {
    const isCurrent = beginRequest();
    if (showLoading) setLoading(true);
    try {
      let apiUrl = '/v1/drivers/with_submission_and_exceed/';
      apiUrl += `?order_by=${sortValue}&page=${currentPage}&page_size=${rowsPerPage}&search=${encodeURIComponent(searchValue)}`;
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, rowsPerPage, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      if (!isCurrent()) return;
      setDriver(response.data.results);
      setExportConditions([['search', searchValue], ['sort', sortValue], ['page', currentPage]]);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, rowsPerPage, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch driver data', error);
      setFetchError(error);
    } finally {
      // RV-043。最新の要求が終われば、静かな取り直しでも読み込み中を解く。静かな取り直しが
      // 最新になると、前の要求（最新でない）も新しい要求（静か）も解かず、表示が残っていた。
      if (isCurrent()) setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchValue, sortValue, beginRequest]);

  const reloadDriverData = useCallback(() => {
    fetchDriverData(false);
  }, [fetchDriverData]);

  useEffect(() => {
    fetchDriverData();
  }, [fetchDriverData]);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (size: number) => {
    setRowsPerPage(size);
    setCurrentPage(1);
  };

  return (
    <section className="bg-white rounded-md p-4 space-y-4">
      <div className="flex flex-row justify-between w-full">
        <Toolbar
          searchValue={searchValue}
          sortValue={sortValue}
          currentPage={currentPage}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          driverData={driver}
          exportConditions={exportConditions}
          onChanged={reloadDriverData}
        />
      </div>
      {/* Loading Indicator */}
      {loading ? (
        <div
          className="flex justify-center items-center"
          style={{ height: '73vh' }}
        >
          <div>{t('ui.loading', { ns: 'common' })}</div>
        </div>
      ) : fetchError ? (
        <FetchFailed error={fetchError} onRetry={() => fetchDriverData()} />
      ) : (
        <div>
          <DriverTable driver={driver} onChanged={reloadDriverData} />
        </div>
      )}
      {/* Pagination Section */}
      <div className="space-y-2">
        <PageSizeSelect id="driver-page-size" value={rowsPerPage} onChange={handlePageSizeChange} />
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </section>
  );
};

export default DriverManagement;
